import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { format, isToday, isYesterday } from 'date-fns';
import { de } from 'date-fns/locale';
import {
    MessageCircle, Send, Search, Plus, Users, MoreVertical, Video,
    Smile, Check, CheckCheck, ArrowLeft, X, UserPlus, Archive,
    Settings, Info, FolderKanban, Phone, Mic, MicOff, VideoOff,
    PhoneOff, Maximize2, Minimize2, Image, Paperclip, Edit3, Trash2,
    Play, Pause, Download, Volume2, Palette, Bell, UsersRound
} from 'lucide-react';

const EMOJI_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '👏', '🎉', '🔥'];
const QUICK_EMOJIS = ['👍', '❤️', '😂', '🔥', '👏'];

// Voice Message Player Component with WhatsApp-style waveform
const VoiceMessagePlayer = ({ src, isOwn, themeColor }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const audioRef = useRef(null);
    const progressInterval = useRef(null);

    // Generate random waveform bars (simulated)
    const waveformBars = useRef(
        Array.from({ length: 35 }, () => Math.random() * 0.7 + 0.3)
    ).current;

    useEffect(() => {
        return () => {
            if (progressInterval.current) {
                clearInterval(progressInterval.current);
            }
        };
    }, []);

    const handlePlayPause = (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
            if (progressInterval.current) {
                clearInterval(progressInterval.current);
            }
        } else {
            audioRef.current.play().catch(() => {});
            setIsPlaying(true);
            progressInterval.current = setInterval(() => {
                if (audioRef.current) {
                    const prog = (audioRef.current.currentTime / audioRef.current.duration) * 100;
                    setProgress(prog);
                    setCurrentTime(audioRef.current.currentTime);
                }
            }, 50);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    const handleEnded = () => {
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime(0);
        if (progressInterval.current) {
            clearInterval(progressInterval.current);
        }
    };

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleWaveformClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        if (!audioRef.current || !duration) return;
        
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percentage = clickX / rect.width;
        const newTime = percentage * duration;
        
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
        setProgress(percentage * 100);
    };

    return (
        <div className="flex items-center gap-3 min-w-[200px] max-w-[280px]" onClick={(e) => e.stopPropagation()}>
            <audio 
                ref={audioRef} 
                src={src} 
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={handleEnded}
                preload="metadata"
            />
            
            {/* Play/Pause Button */}
            <button
                onClick={handlePlayPause}
                className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all ${
                    isOwn 
                        ? 'bg-white/20 hover:bg-white/30' 
                        : 'bg-primary/10 hover:bg-primary/20'
                }`}
            >
                {isPlaying ? (
                    <Pause className={`w-5 h-5 ${isOwn ? 'text-white' : 'text-primary'}`} />
                ) : (
                    <Play className={`w-5 h-5 ml-0.5 ${isOwn ? 'text-white' : 'text-primary'}`} />
                )}
            </button>
            
            {/* Waveform */}
            <div className="flex-1 flex flex-col gap-1">
                <div 
                    className="flex items-center gap-[2px] h-8 cursor-pointer"
                    onClick={handleWaveformClick}
                >
                    {waveformBars.map((height, i) => {
                        const barProgress = (i / waveformBars.length) * 100;
                        const isFilled = barProgress <= progress;
                        return (
                            <div
                                key={i}
                                className={`w-[3px] rounded-full transition-all duration-75 ${
                                    isFilled 
                                        ? (isOwn ? 'bg-white' : 'bg-primary')
                                        : (isOwn ? 'bg-white/30' : 'bg-primary/30')
                                }`}
                                style={{ height: `${height * 100}%` }}
                            />
                        );
                    })}
                </div>
                
                {/* Time */}
                <div className={`text-[10px] ${isOwn ? 'text-white/70' : 'text-muted-foreground'}`}>
                    {formatTime(isPlaying ? currentTime : duration)}
                </div>
            </div>
        </div>
    );
};

const CHAT_THEMES = [
    { id: 'default', name: 'Standard', bubbleColor: '#009dde', bg: 'bg-gradient-to-b from-muted/30 to-background' },
    { id: 'dark', name: 'Dunkel', bubbleColor: '#6366f1', bg: 'bg-gradient-to-b from-slate-900 to-slate-800' },
    { id: 'nature', name: 'Natur', bubbleColor: '#22c55e', bg: 'bg-gradient-to-b from-green-50 to-emerald-100 dark:from-green-950 dark:to-emerald-900' },
    { id: 'sunset', name: 'Sonnenuntergang', bubbleColor: '#f97316', bg: 'bg-gradient-to-b from-orange-50 to-rose-100 dark:from-orange-950 dark:to-rose-900' },
    { id: 'ocean', name: 'Ozean', bubbleColor: '#0ea5e9', bg: 'bg-gradient-to-b from-cyan-50 to-blue-100 dark:from-cyan-950 dark:to-blue-900' },
    { id: 'rose', name: 'Rose', bubbleColor: '#ec4899', bg: 'bg-gradient-to-b from-pink-50 to-rose-100 dark:from-pink-950 dark:to-rose-900' },
    { id: 'minimal', name: 'Minimal', bubbleColor: '#64748b', bg: 'bg-background' }
];

const ChatPage = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [conversations, setConversations] = useState([]);
    const [archivedConversations, setArchivedConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showNewChat, setShowNewChat] = useState(false);
    const [showNewGroup, setShowNewGroup] = useState(false);
    const [showChatInfo, setShowChatInfo] = useState(false);
    const [showChatSettings, setShowChatSettings] = useState(false);
    const [showThemes, setShowThemes] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [onlineUsers, setOnlineUsers] = useState({});
    const [typingUsers, setTypingUsers] = useState({});
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [chatInfoData, setChatInfoData] = useState(null);
    const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
    const [groupName, setGroupName] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [inVideoCall, setInVideoCall] = useState(false);
    const [activeCall, setActiveCall] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [editContent, setEditContent] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [chatSettings, setChatSettings] = useState({ theme: 'default', sound_enabled: true });
    const [teamChat, setTeamChat] = useState(null);
    const [showUserInfo, setShowUserInfo] = useState(null); // User info popup
    const [incomingCall, setIncomingCall] = useState(null); // Incoming call notification
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(false);
    const [localStream, setLocalStream] = useState(null);
    const [remoteStreams, setRemoteStreams] = useState({});
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const notificationSoundRef = useRef(null);
    const callSoundRef = useRef(null);
    const localVideoRef = useRef(null);
    const peerConnectionsRef = useRef({});
    const wsRef = useRef(null);

    // WebRTC configuration with STUN servers
    const rtcConfig = {
        iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'stun:stun2.l.google.com:19302' },
        ]
    };

    const currentTheme = CHAT_THEMES.find(t => t.id === chatSettings.theme) || CHAT_THEMES[0];

    // Handle URL params
    useEffect(() => {
        const userId = searchParams.get('user');
        const convId = searchParams.get('conversation');
        const callId = searchParams.get('call');
        
        if (userId && user) {
            startConversationWithUser(userId);
        } else if (convId) {
            loadConversation(convId);
            if (callId) {
                joinVideoCall(callId);
            }
        }
    }, [searchParams, user]);

    // Responsive
    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Connect local stream to video element when it changes
    useEffect(() => {
        if (localStream && localVideoRef.current) {
            localVideoRef.current.srcObject = localStream;
            // Ensure video plays
            localVideoRef.current.play().catch(e => console.log('Video play error:', e));
        }
    }, [localStream, inVideoCall]);

    // Load chat settings
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const res = await api.get('/users/me/chat-settings');
                setChatSettings(res.data);
            } catch (e) {}
        };
        loadSettings();
    }, [api]);

    const fetchConversations = useCallback(async () => {
        try {
            const [convRes, teamRes] = await Promise.all([
                api.get('/chat/conversations'),
                api.get('/chat/team').catch(() => null)
            ]);
            
            let allConvs = convRes.data;
            
            // Add team chat to top if exists
            if (teamRes?.data) {
                setTeamChat(teamRes.data);
                // Remove team chat from list if already there, then add to top
                allConvs = allConvs.filter(c => c.id !== teamRes.data.id);
                allConvs = [{ ...teamRes.data, is_pinned: true }, ...allConvs];
            }
            
            // Sort: pinned first, then by last_message_at
            allConvs.sort((a, b) => {
                if (a.is_pinned && !b.is_pinned) return -1;
                if (!a.is_pinned && b.is_pinned) return 1;
                return new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0);
            });
            
            const active = allConvs.filter(c => !c.is_archived);
            const archived = allConvs.filter(c => c.is_archived);
            setConversations(active);
            setArchivedConversations(archived);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);

    const fetchUsers = async () => {
        try {
            const res = await api.get('/users');
            setAllUsers(res.data.filter(u => u.id !== user?.id));
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchOnlineStatus = async () => {
        try {
            const res = await api.get('/chat/online-status');
            setOnlineUsers(res.data);
        } catch (error) {}
    };

    useEffect(() => {
        fetchConversations();
        fetchUsers();
        fetchOnlineStatus();
        
        const interval = setInterval(() => {
            fetchConversations();
            fetchOnlineStatus();
            checkIncomingCalls();
            if (activeConversation) {
                loadMessages(activeConversation.id, false);
                fetchTypingStatus(activeConversation.id);
            }
        }, 3000);
        
        return () => clearInterval(interval);
    }, [fetchConversations, activeConversation]);

    // Check for incoming calls
    const checkIncomingCalls = async () => {
        try {
            const res = await api.get('/chat/incoming-calls');
            if (res.data.length > 0 && !incomingCall && !inVideoCall) {
                const call = res.data[0];
                setIncomingCall(call);
                
                // Play ringtone
                if (chatSettings.sound_enabled) {
                    try {
                        const audio = new Audio('data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYNbPkEAAAAAAD/+xBkAA/wAABpAAAACAAADSAAAAEAAAGkAAAAIAAANIAAAARMQU1FMy4xMDBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV');
                        audio.volume = 0.7;
                        audio.loop = true;
                        callSoundRef.current = audio;
                        audio.play().catch(() => {});
                    } catch (e) {}
                }
                
                // Show toast notification with action buttons
                toast(
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Avatar className="w-12 h-12">
                                {call.caller?.profile_image ? (
                                    <AvatarImage src={call.caller.profile_image} />
                                ) : (
                                    <AvatarFallback style={{ backgroundColor: call.caller?.profile_color || '#009dde' }} className="text-white">
                                        {call.caller?.first_name?.[0]}{call.caller?.last_name?.[0]}
                                    </AvatarFallback>
                                )}
                            </Avatar>
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                                <Video className="w-3 h-3 text-white" />
                            </div>
                        </div>
                        <div className="flex-1">
                            <p className="font-semibold">{call.caller?.first_name} {call.caller?.last_name}</p>
                            <p className="text-sm text-muted-foreground">Eingehender Video-Anruf</p>
                            <div className="flex gap-2 mt-2">
                                <Button size="sm" variant="destructive" className="h-8 px-3 rounded-full" onClick={() => { toast.dismiss('incoming-call'); declineIncomingCall(); }}>
                                    <PhoneOff className="w-4 h-4 mr-1" /> Ablehnen
                                </Button>
                                <Button size="sm" className="h-8 px-3 rounded-full bg-green-500 hover:bg-green-600" onClick={() => { toast.dismiss('incoming-call'); acceptIncomingCall(); }}>
                                    <Video className="w-4 h-4 mr-1" /> Abheben
                                </Button>
                            </div>
                        </div>
                    </div>,
                    { 
                        position: 'top-right', 
                        duration: 60000,
                        id: 'incoming-call',
                        style: { marginTop: '70px' },
                        className: 'border-green-500/30 !bg-card'
                    }
                );
            }
        } catch (e) {}
    };

    // Handle incoming call actions
    const acceptIncomingCall = async () => {
        if (!incomingCall) return;
        
        // Stop ringtone
        if (callSoundRef.current) {
            callSoundRef.current.pause();
            callSoundRef.current = null;
        }
        toast.dismiss('incoming-call');
        
        await joinVideoCall(incomingCall.id);
        setIncomingCall(null);
    };

    const declineIncomingCall = async () => {
        if (!incomingCall) return;
        
        // Stop ringtone
        if (callSoundRef.current) {
            callSoundRef.current.pause();
            callSoundRef.current = null;
        }
        toast.dismiss('incoming-call');
        
        try {
            await api.post(`/chat/video-call/${incomingCall.id}/decline`);
        } catch (e) {}
        
        setIncomingCall(null);
    };

    // Check for new messages and show notification toast
    const prevMessagesRef = useRef([]);
    useEffect(() => {
        if (messages.length > prevMessagesRef.current.length && prevMessagesRef.current.length > 0) {
            const newMsg = messages[messages.length - 1];
            if (newMsg && newMsg.sender_id !== user?.id) {
                // Play notification sound
                if (chatSettings.sound_enabled) {
                    try {
                        const audio = new Audio('data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYNbPkEAAAAAAD/+xBkAA/wAABpAAAACAAADSAAAAEAAAGkAAAAIAAANIAAAARMQU1FMy4xMDBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV');
                        audio.volume = 0.5;
                        audio.play().catch(() => {});
                    } catch (e) {}
                }
                
                // Show toast for incoming message - klickbar zum Chat
                toast(
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => {
                        toast.dismiss();
                        if (newMsg.conversation_id) {
                            navigate(`/chat?conversation=${newMsg.conversation_id}`);
                            if (newMsg.conversation_id !== activeConversation?.id) {
                                loadConversation(newMsg.conversation_id);
                            }
                        }
                    }}>
                        <Avatar className="w-10 h-10">
                            {newMsg.sender?.profile_image ? (
                                <AvatarImage src={newMsg.sender.profile_image} />
                            ) : (
                                <AvatarFallback style={{ backgroundColor: newMsg.sender?.profile_color || '#009dde' }} className="text-white text-sm">
                                    {newMsg.sender?.first_name?.[0]}{newMsg.sender?.last_name?.[0]}
                                </AvatarFallback>
                            )}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold">{newMsg.sender?.first_name} {newMsg.sender?.last_name}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {newMsg.message_type === 'voice' ? '🎤 Sprachnachricht' : 
                                 newMsg.message_type === 'image' ? '📷 Bild' :
                                 newMsg.message_type === 'file' ? '📎 Datei' :
                                 newMsg.content?.substring(0, 50)}
                            </p>
                        </div>
                        <MessageCircle className="w-5 h-5 text-primary shrink-0" />
                    </div>,
                    { 
                        position: 'top-right', 
                        duration: 4000,
                        style: { marginTop: '70px' },
                        className: 'hover:scale-[1.02] transition-transform'
                    }
                );
            }
        }
        prevMessagesRef.current = messages;
    }, [messages, user?.id, chatSettings.sound_enabled, activeConversation?.id]);

    const fetchTypingStatus = async (convId) => {
        try {
            const res = await api.get(`/chat/conversations/${convId}/typing`);
            setTypingUsers(prev => ({ ...prev, [convId]: res.data.typing_users }));
        } catch (error) {}
    };

    const sendTypingIndicator = async () => {
        if (!activeConversation) return;
        try {
            await api.post(`/chat/conversations/${activeConversation.id}/typing`);
        } catch (error) {}
    };

    const startConversationWithUser = async (userId) => {
        try {
            const res = await api.get(`/chat/start/${userId}`);
            setActiveConversation(res.data);
            await loadMessages(res.data.id);
            setSearchParams({});
            fetchConversations();
        } catch (error) {
            toast.error('Fehler beim Starten der Konversation');
        }
    };

    const loadConversation = async (convId) => {
        try {
            const res = await api.get(`/chat/conversations/${convId}`);
            setActiveConversation(res.data);
            await loadMessages(convId);
        } catch (error) {
            toast.error('Konversation nicht gefunden');
        }
    };

    const loadMessages = async (convId, scroll = true) => {
        try {
            const res = await api.get(`/chat/conversations/${convId}/messages`);
            setMessages(res.data);
            if (scroll) {
                setTimeout(() => scrollToBottom(), 100);
            }
        } catch (error) {}
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleInputChange = (e) => {
        setNewMessage(e.target.value);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        sendTypingIndicator();
        typingTimeoutRef.current = setTimeout(() => {}, 2000);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !activeConversation || sendingMessage) return;
        
        setSendingMessage(true);
        try {
            await api.post(`/chat/conversations/${activeConversation.id}/messages`, {
                content: newMessage.trim()
            });
            setNewMessage('');
            await loadMessages(activeConversation.id);
            fetchConversations();
            if (chatSettings.sound_enabled) {
                // Play send sound
                const audio = new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU');
                audio.volume = 0.3;
                audio.play().catch(() => {});
            }
        } catch (error) {
            toast.error('Fehler beim Senden');
        } finally {
            setSendingMessage(false);
        }
    };

    const handleEditMessage = async () => {
        if (!editingMessage || !editContent.trim()) return;
        try {
            await api.put(`/chat/messages/${editingMessage.id}`, { content: editContent.trim() });
            setEditingMessage(null);
            setEditContent('');
            await loadMessages(activeConversation.id, false);
            toast.success('Nachricht bearbeitet');
        } catch (error) {
            toast.error('Fehler');
        }
    };

    const handleDeleteMessage = async (messageId) => {
        if (!confirm('Nachricht wirklich löschen?')) return;
        try {
            await api.delete(`/chat/messages/${messageId}`);
            await loadMessages(activeConversation.id, false);
            toast.success('Nachricht gelöscht');
        } catch (error) {
            toast.error('Fehler');
        }
    };

    const handleReaction = async (messageId, emoji) => {
        try {
            await api.post(`/chat/messages/${messageId}/reactions`, { emoji });
            await loadMessages(activeConversation.id, false);
        } catch (error) {}
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !activeConversation) return;
        
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            toast.loading('Wird hochgeladen...');
            await api.post(`/chat/conversations/${activeConversation.id}/files`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.dismiss();
            toast.success('Datei gesendet');
            await loadMessages(activeConversation.id);
            fetchConversations();
        } catch (error) {
            toast.dismiss();
            toast.error('Upload fehlgeschlagen');
        }
        e.target.value = '';
    };

    const startVoiceRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            const chunks = [];
            
            mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
            mediaRecorder.onstop = async () => {
                const blob = new Blob(chunks, { type: 'audio/webm' });
                const formData = new FormData();
                formData.append('file', blob, 'voice.webm');
                
                try {
                    await api.post(`/chat/conversations/${activeConversation.id}/voice`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    await loadMessages(activeConversation.id);
                    fetchConversations();
                } catch (error) {
                    toast.error('Fehler beim Senden der Sprachnachricht');
                }
                
                stream.getTracks().forEach(track => track.stop());
            };
            
            mediaRecorder.start();
            setIsRecording(true);
            toast.info('Aufnahme gestartet...');
        } catch (error) {
            toast.error('Mikrofon-Zugriff verweigert');
        }
    };

    const stopVoiceRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const handleArchiveChat = async (convId) => {
        try {
            await api.post(`/chat/conversations/${convId}/archive`);
            toast.success('Chat archiviert');
            fetchConversations();
            if (activeConversation?.id === convId) {
                setActiveConversation(null);
            }
        } catch (error) {
            toast.error('Fehler');
        }
    };

    const handleCreateGroup = async () => {
        if (!groupName.trim() || selectedGroupMembers.length < 1) {
            toast.error('Gruppenname und mindestens 1 Teilnehmer benötigt');
            return;
        }
        try {
            const res = await api.post('/chat/conversations', {
                participant_ids: selectedGroupMembers,
                name: groupName.trim(),
                is_group: true
            });
            setShowNewGroup(false);
            setGroupName('');
            setSelectedGroupMembers([]);
            setActiveConversation(res.data);
            await loadMessages(res.data.id);
            fetchConversations();
            toast.success('Gruppe erstellt!');
        } catch (error) {
            toast.error('Fehler');
        }
    };

    const handleStartNewChat = async (targetUser) => {
        setShowNewChat(false);
        await startConversationWithUser(targetUser.id);
    };

    const loadChatInfo = async () => {
        if (!activeConversation) return;
        try {
            const res = await api.get(`/chat/conversations/${activeConversation.id}/info`);
            setChatInfoData(res.data);
            setShowChatInfo(true);
        } catch (error) {
            toast.error('Fehler');
        }
    };

    // Initialize WebRTC media stream
    const initLocalStream = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: true, 
                audio: true 
            });
            setLocalStream(stream);
            if (localVideoRef.current) {
                localVideoRef.current.srcObject = stream;
            }
            return stream;
        } catch (error) {
            toast.error('Kamera/Mikrofon Zugriff verweigert');
            console.error('Error getting media stream:', error);
            return null;
        }
    };

    // Create peer connection for a participant
    const createPeerConnection = (participantId, isInitiator, stream) => {
        const pc = new RTCPeerConnection(rtcConfig);
        
        // Add local tracks
        if (stream) {
            stream.getTracks().forEach(track => {
                pc.addTrack(track, stream);
            });
        }
        
        // Handle incoming tracks (remote video)
        pc.ontrack = (event) => {
            setRemoteStreams(prev => ({
                ...prev,
                [participantId]: event.streams[0]
            }));
        };
        
        // Handle ICE candidates
        pc.onicecandidate = (event) => {
            if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                    type: 'ice-candidate',
                    candidate: event.candidate,
                    target: participantId,
                    from: user?.id
                }));
            }
        };
        
        pc.oniceconnectionstatechange = () => {
            if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
                setRemoteStreams(prev => {
                    const updated = { ...prev };
                    delete updated[participantId];
                    return updated;
                });
            }
        };
        
        peerConnectionsRef.current[participantId] = pc;
        return pc;
    };

    // Start video call with WebRTC
    const startVideoCall = async () => {
        if (!activeConversation) return;
        
        try {
            // Get local media stream
            const stream = await initLocalStream();
            if (!stream) return;
            
            // Create call on server
            const res = await api.post(`/chat/video-call/${activeConversation.id}/start`);
            setActiveCall(res.data);
            setInVideoCall(true);
            
            // Connect to WebSocket signaling
            const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') || 'ws://localhost:8001';
            const ws = new WebSocket(`${wsUrl}/ws/video/${res.data.id}`);
            wsRef.current = ws;
            
            ws.onopen = () => {
                ws.send(JSON.stringify({ 
                    type: 'join', 
                    userId: user?.id,
                    callId: res.data.id
                }));
            };
            
            ws.onmessage = async (event) => {
                const data = JSON.parse(event.data);
                await handleSignalingMessage(data, stream);
            };
            
            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };
            
            toast.success('Video-Call gestartet');
        } catch (error) {
            toast.error('Fehler beim Starten des Calls');
            console.error(error);
        }
    };

    // Handle signaling messages (offer/answer/ice-candidate)
    const handleSignalingMessage = async (data, stream) => {
        const { type, from, offer, answer, candidate } = data;
        
        if (type === 'user-joined' && from !== user?.id) {
            // New user joined, create offer
            const pc = createPeerConnection(from, true, stream || localStream);
            const newOffer = await pc.createOffer();
            await pc.setLocalDescription(newOffer);
            
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                    type: 'offer',
                    offer: newOffer,
                    target: from,
                    from: user?.id
                }));
            }
        }
        
        if (type === 'offer' && from !== user?.id) {
            // Received offer, create answer
            let pc = peerConnectionsRef.current[from];
            if (!pc) {
                pc = createPeerConnection(from, false, stream || localStream);
            }
            
            await pc.setRemoteDescription(new RTCSessionDescription(offer));
            const answerDesc = await pc.createAnswer();
            await pc.setLocalDescription(answerDesc);
            
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                    type: 'answer',
                    answer: answerDesc,
                    target: from,
                    from: user?.id
                }));
            }
        }
        
        if (type === 'answer' && from !== user?.id) {
            const pc = peerConnectionsRef.current[from];
            if (pc) {
                await pc.setRemoteDescription(new RTCSessionDescription(answer));
            }
        }
        
        if (type === 'ice-candidate' && from !== user?.id) {
            const pc = peerConnectionsRef.current[from];
            if (pc && candidate) {
                try {
                    await pc.addIceCandidate(new RTCIceCandidate(candidate));
                } catch (e) {
                    console.error('Error adding ICE candidate:', e);
                }
            }
        }
        
        if (type === 'user-left') {
            // User left, clean up their connection
            const pc = peerConnectionsRef.current[from];
            if (pc) {
                pc.close();
                delete peerConnectionsRef.current[from];
            }
            setRemoteStreams(prev => {
                const updated = { ...prev };
                delete updated[from];
                return updated;
            });
        }
    };

    const joinVideoCall = async (callId) => {
        try {
            // Get local media stream first
            const stream = await initLocalStream();
            if (!stream) return;
            
            const res = await api.post(`/chat/video-call/${callId}/join`);
            setActiveCall(res.data.call);
            setInVideoCall(true);
            
            // Connect to WebSocket signaling
            const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') || 'ws://localhost:8001';
            const ws = new WebSocket(`${wsUrl}/ws/video/${callId}`);
            wsRef.current = ws;
            
            ws.onopen = () => {
                ws.send(JSON.stringify({ 
                    type: 'join', 
                    userId: user?.id,
                    callId: callId
                }));
            };
            
            ws.onmessage = async (event) => {
                const data = JSON.parse(event.data);
                await handleSignalingMessage(data, stream);
            };
            
            toast.success('Dem Call beigetreten');
        } catch (error) {
            toast.error('Call nicht mehr aktiv');
        }
    };

    const endVideoCall = async () => {
        // Stop local stream
        if (localStream) {
            localStream.getTracks().forEach(track => track.stop());
            setLocalStream(null);
        }
        
        // Close all peer connections
        Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
        peerConnectionsRef.current = {};
        setRemoteStreams({});
        
        // Close WebSocket
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        
        // Notify server
        if (activeCall) {
            try {
                await api.post(`/chat/video-call/${activeCall.id}/leave`);
            } catch (e) {}
        }
        
        setInVideoCall(false);
        setActiveCall(null);
        setIsMuted(false);
        setIsVideoOff(false);
        toast.info('Call beendet');
    };

    // Toggle mute/unmute
    const toggleMute = () => {
        if (localStream) {
            const audioTrack = localStream.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled;
                setIsMuted(!audioTrack.enabled);
            }
        }
    };

    // Toggle video on/off
    const toggleVideo = () => {
        if (localStream) {
            const videoTrack = localStream.getVideoTracks()[0];
            if (videoTrack) {
                videoTrack.enabled = !videoTrack.enabled;
                setIsVideoOff(!videoTrack.enabled);
            }
        }
    };

    const updateChatSettings = async (newSettings) => {
        const updated = { ...chatSettings, ...newSettings };
        setChatSettings(updated);
        try {
            await api.put('/users/me/chat-settings', updated);
        } catch (e) {}
    };

    const getConversationName = (conv) => {
        if (conv.is_team_chat) return '👥 Team-Chat';
        if (conv.is_group) return conv.name;
        const other = conv.participant_details?.[0];
        return other ? `${other.first_name} ${other.last_name}` : 'Unbekannt';
    };

    const getConversationAvatar = (conv) => {
        if (conv.is_group || conv.is_team_chat) return null;
        return conv.participant_details?.[0];
    };

    const isUserOnline = (userId) => onlineUsers[userId]?.is_online || false;

    const getTypingText = () => {
        const typing = typingUsers[activeConversation?.id] || [];
        if (typing.length === 0) return null;
        if (typing.length === 1) return `${typing[0]} schreibt...`;
        return `${typing.length} Personen schreiben...`;
    };

    const formatMessageDate = (dateStr) => {
        const date = new Date(dateStr);
        if (isToday(date)) return format(date, 'HH:mm');
        if (isYesterday(date)) return 'Gestern';
        const now = new Date();
        if (date.getFullYear() === now.getFullYear()) return format(date, 'dd. MMM', { locale: de });
        return format(date, 'dd.MM.yy');
    };

    const filteredConversations = (showArchived ? archivedConversations : conversations).filter(conv => {
        if (!searchQuery) return true;
        const name = getConversationName(conv).toLowerCase();
        return name.includes(searchQuery.toLowerCase());
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-88px)]">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-56px)] md:h-[calc(100vh-72px)] flex bg-background" data-testid="chat-page">
            {/* Conversations List */}
            <div className={`${isMobileView && activeConversation ? 'hidden' : 'flex'} flex-col w-full md:w-[340px] lg:w-[400px] border-r border-border bg-card shrink-0`}>
                <div className="p-4 border-b border-border shrink-0">
                    <div className="flex items-center justify-between mb-3">
                        <h1 className="text-xl font-bold flex items-center gap-2">
                            <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
                                <MessageCircle className="w-4 h-4 text-primary" />
                            </div>
                            Chats
                        </h1>
                        <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-9 w-9 rounded-xl hover:bg-primary/10 hover:text-primary" onClick={() => setShowThemes(true)} title="Themes">
                                <Palette className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-9 w-9 rounded-xl hover:bg-primary/10 hover:text-primary" onClick={() => setShowNewGroup(true)} title="Neue Gruppe">
                                <Users className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="default" className="h-9 w-9 rounded-xl shadow-sm" onClick={() => setShowNewChat(true)} data-testid="new-chat-btn">
                                <Plus className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input placeholder="Chats durchsuchen..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 bg-muted/40 border-0 rounded-xl h-10" />
                    </div>
                    <div className="flex gap-2 mt-3">
                        <Button size="sm" variant={!showArchived ? "default" : "ghost"} onClick={() => setShowArchived(false)} className="flex-1 rounded-xl h-9">
                            Aktiv ({conversations.length})
                        </Button>
                        <Button size="sm" variant={showArchived ? "default" : "ghost"} onClick={() => setShowArchived(true)} className="flex-1 gap-1 rounded-xl h-9">
                            <Archive className="w-3 h-3" />
                            Archiv ({archivedConversations.length})
                        </Button>
                    </div>
                </div>
                
                <ScrollArea className="flex-1">
                    {filteredConversations.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                            <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-20" />
                            <p className="font-medium">{showArchived ? 'Keine archivierten Chats' : 'Keine Konversationen'}</p>
                        </div>
                    ) : (
                        <div className="p-2">
                            {filteredConversations.map(conv => {
                                const other = getConversationAvatar(conv);
                                const isActive = activeConversation?.id === conv.id;
                                const online = other ? isUserOnline(other.id) : false;
                                
                                return (
                                    <div
                                        key={conv.id}
                                        className={`flex items-center gap-3 px-3 py-3 cursor-pointer rounded-xl mb-0.5 transition-all duration-150
                                            ${isActive 
                                                ? 'bg-primary/10 shadow-sm' 
                                                : 'hover:bg-muted/60'
                                            }`}
                                        onClick={() => { setActiveConversation(conv); loadMessages(conv.id); }}
                                        data-testid={`conversation-${conv.id}`}
                                    >
                                        {/* Avatar */}
                                        <div className="relative shrink-0">
                                            <Avatar className={`w-12 h-12 ${isActive ? 'ring-2 ring-primary/30' : ''}`}>
                                                {other?.profile_image ? (
                                                    <AvatarImage src={other.profile_image} className="object-cover" />
                                                ) : (
                                                    <AvatarFallback style={{ backgroundColor: conv.is_team_chat ? '#22c55e' : (other?.profile_color || '#009dde') }} className="text-white font-semibold text-sm">
                                                        {conv.is_team_chat ? <UsersRound className="w-5 h-5" /> : conv.is_group ? <Users className="w-5 h-5" /> : `${other?.first_name?.[0] || ''}${other?.last_name?.[0] || ''}`}
                                                    </AvatarFallback>
                                                )}
                                            </Avatar>
                                            {online && !conv.is_group && !conv.is_team_chat && (
                                                <div className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-card shadow-sm" />
                                            )}
                                            {conv.is_pinned && (
                                                <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-400 flex items-center justify-center shadow-sm">
                                                    <span className="text-[8px]">📌</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className={`font-semibold truncate text-sm ${isActive ? 'text-primary' : ''} ${conv.unread_count > 0 ? 'text-foreground' : ''}`}>
                                                    {getConversationName(conv)}
                                                </span>
                                                <span className={`text-[11px] shrink-0 ${conv.unread_count > 0 ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                                                    {conv.last_message_at && formatMessageDate(conv.last_message_at)}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between gap-2 mt-0.5">
                                                <p className={`text-[13px] truncate ${conv.unread_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                                                    {conv.last_message || 'Keine Nachrichten'}
                                                </p>
                                                {conv.unread_count > 0 && (
                                                    <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center shrink-0 shadow-sm">
                                                        {conv.unread_count}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </ScrollArea>
            </div>
            
            {/* Chat View */}
            <div className={`${isMobileView && !activeConversation ? 'hidden' : 'flex'} flex-1 flex-col overflow-hidden`}>
                {activeConversation ? (
                    <>
                        {/* Header */}
                        <div className="px-4 py-2.5 border-b border-border flex items-center gap-3 shrink-0" style={{ background: `linear-gradient(135deg, ${currentTheme.bubbleColor}08, ${currentTheme.bubbleColor}04)` }}>
                            {isMobileView && (
                                <Button variant="ghost" size="icon" className="rounded-xl shrink-0" onClick={() => setActiveConversation(null)}>
                                    <ArrowLeft className="w-5 h-5" />
                                </Button>
                            )}
                            <Avatar className="w-10 h-10 shrink-0 ring-2 ring-primary/10">
                                {getConversationAvatar(activeConversation)?.profile_image ? (
                                    <AvatarImage src={getConversationAvatar(activeConversation).profile_image} className="object-cover" />
                                ) : (
                                    <AvatarFallback style={{ backgroundColor: activeConversation.is_team_chat ? '#22c55e' : (getConversationAvatar(activeConversation)?.profile_color || '#009dde') }} className="text-white font-semibold">
                                        {activeConversation.is_team_chat ? <UsersRound className="w-4 h-4" /> : activeConversation.is_group ? <Users className="w-4 h-4" /> : `${getConversationAvatar(activeConversation)?.first_name?.[0] || ''}${getConversationAvatar(activeConversation)?.last_name?.[0] || ''}`}
                                    </AvatarFallback>
                                )}
                            </Avatar>
                            <div className="flex-1 min-w-0">
                                <p className="font-bold truncate text-sm">{getConversationName(activeConversation)}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                    {getTypingText() ? (
                                        <span className="text-primary font-medium">{getTypingText()}</span>
                                    ) : (
                                        activeConversation.is_team_chat ? 'Alle Teammitglieder' :
                                        activeConversation.is_group ? `${activeConversation.participants?.length} Teilnehmer` : 
                                        isUserOnline(getConversationAvatar(activeConversation)?.id) ? (
                                            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />Online</span>
                                        ) : 'Offline'
                                    )}
                                </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl hover:bg-primary/10 hover:text-primary" onClick={startVideoCall} title="Video-Call">
                                    <Video className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl hover:bg-primary/10 hover:text-primary" onClick={loadChatInfo} title="Info">
                                    <Info className="w-4 h-4" />
                                </Button>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl hover:bg-primary/10">
                                            <MoreVertical className="w-4 h-4" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent align="end" className="w-48 p-1 rounded-xl">
                                        <Button variant="ghost" className="w-full justify-start gap-2 text-sm rounded-lg" onClick={() => setShowThemes(true)}>
                                            <Palette className="w-4 h-4" />
                                            Theme ändern
                                        </Button>
                                        {!activeConversation.is_team_chat && (
                                            <Button variant="ghost" className="w-full justify-start gap-2 text-sm rounded-lg" onClick={() => handleArchiveChat(activeConversation.id)}>
                                                <Archive className="w-4 h-4" />
                                                Archivieren
                                            </Button>
                                        )}
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                        
                        {/* Video Call Overlay with WebRTC */}
                        <AnimatePresence>
                            {inVideoCall && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute inset-0 bg-black/95 z-50 flex flex-col"
                                >
                                    {/* Video Grid */}
                                    <div className="flex-1 p-4 flex flex-wrap gap-4 justify-center items-center content-center overflow-auto">
                                        {/* Local Video (Self) */}
                                        <div className="relative w-full max-w-md aspect-video bg-gray-800 rounded-2xl overflow-hidden shadow-2xl">
                                            <video
                                                ref={localVideoRef}
                                                autoPlay
                                                playsInline
                                                muted
                                                className={`w-full h-full object-cover ${isVideoOff ? 'hidden' : ''}`}
                                            />
                                            {isVideoOff && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                                                    <Avatar className="w-24 h-24">
                                                        <AvatarFallback className="text-3xl bg-primary">
                                                            {user?.first_name?.[0]}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                </div>
                                            )}
                                            <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-lg">
                                                <span className="text-white text-sm font-medium">Du</span>
                                                {isMuted && <MicOff className="w-4 h-4 text-red-500" />}
                                            </div>
                                        </div>
                                        
                                        {/* Remote Videos */}
                                        {Object.entries(remoteStreams).map(([participantId, stream]) => (
                                            <div key={participantId} className="relative w-full max-w-md aspect-video bg-gray-700 rounded-2xl overflow-hidden shadow-2xl">
                                                <video
                                                    autoPlay
                                                    playsInline
                                                    className="w-full h-full object-cover"
                                                    ref={(video) => {
                                                        if (video && stream) {
                                                            video.srcObject = stream;
                                                        }
                                                    }}
                                                />
                                                <div className="absolute bottom-3 left-3 bg-black/60 px-3 py-1.5 rounded-lg">
                                                    <span className="text-white text-sm">Teilnehmer</span>
                                                </div>
                                            </div>
                                        ))}
                                        
                                        {/* Show placeholder if no remote streams */}
                                        {Object.keys(remoteStreams).length === 0 && (
                                            <div className="text-white text-center py-8">
                                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-700 flex items-center justify-center">
                                                    <Users className="w-8 h-8 text-gray-400" />
                                                </div>
                                                <p className="text-gray-400">Warte auf Teilnehmer...</p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Controls */}
                                    <div className="p-6 flex justify-center gap-4 bg-gradient-to-t from-black/80 to-transparent">
                                        <Button 
                                            size="lg" 
                                            variant="outline" 
                                            className={`rounded-full w-14 h-14 border-gray-700 transition-all ${
                                                isMuted ? 'bg-red-500/20 border-red-500 text-red-500' : 'bg-gray-800 hover:bg-gray-700 text-white'
                                            }`}
                                            onClick={toggleMute}
                                        >
                                            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                                        </Button>
                                        <Button 
                                            size="lg" 
                                            variant="outline" 
                                            className={`rounded-full w-14 h-14 border-gray-700 transition-all ${
                                                isVideoOff ? 'bg-red-500/20 border-red-500 text-red-500' : 'bg-gray-800 hover:bg-gray-700 text-white'
                                            }`}
                                            onClick={toggleVideo}
                                        >
                                            {isVideoOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
                                        </Button>
                                        <Button 
                                            size="lg" 
                                            variant="destructive" 
                                            className="rounded-full w-14 h-14 shadow-lg shadow-red-500/30" 
                                            onClick={endVideoCall}
                                        >
                                            <PhoneOff className="w-6 h-6" />
                                        </Button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        
                        {/* Incoming Call Overlay */}
                        <AnimatePresence>
                            {incomingCall && !inVideoCall && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    className="absolute inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
                                >
                                    <div className="bg-card rounded-3xl p-8 shadow-2xl max-w-sm w-full mx-4 text-center">
                                        <div className="relative mx-auto w-24 h-24 mb-6">
                                            <Avatar className="w-24 h-24 ring-4 ring-green-500/30">
                                                {incomingCall.caller?.profile_image ? (
                                                    <AvatarImage src={incomingCall.caller.profile_image} />
                                                ) : (
                                                    <AvatarFallback style={{ backgroundColor: incomingCall.caller?.profile_color || '#009dde' }} className="text-white text-2xl">
                                                        {incomingCall.caller?.first_name?.[0]}{incomingCall.caller?.last_name?.[0]}
                                                    </AvatarFallback>
                                                )}
                                            </Avatar>
                                            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-green-500 rounded-full animate-pulse">
                                                <Video className="w-4 h-4 text-white" />
                                            </div>
                                        </div>
                                        
                                        <h2 className="text-xl font-bold mb-1">
                                            {incomingCall.caller?.first_name} {incomingCall.caller?.last_name}
                                        </h2>
                                        <p className="text-muted-foreground mb-6">Eingehender Video-Anruf</p>
                                        
                                        <div className="flex gap-4 justify-center">
                                            <Button
                                                size="lg"
                                                variant="destructive"
                                                className="rounded-full w-16 h-16"
                                                onClick={declineIncomingCall}
                                            >
                                                <PhoneOff className="w-7 h-7" />
                                            </Button>
                                            <Button
                                                size="lg"
                                                className="rounded-full w-16 h-16 bg-green-500 hover:bg-green-600"
                                                onClick={acceptIncomingCall}
                                            >
                                                <Video className="w-7 h-7" />
                                            </Button>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        
                        {/* Messages */}
                        <ScrollArea className="flex-1 overflow-hidden">
                            <div className="px-4 py-6 space-y-1 min-h-full" style={{ background: `radial-gradient(ellipse at 50% 0%, ${currentTheme.bubbleColor}06, transparent 70%)` }}>
                                {messages.length === 0 && (
                                    <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                                        <MessageCircle className="w-16 h-16 mb-4 opacity-20" />
                                        <p className="text-lg font-medium">Noch keine Nachrichten</p>
                                        <p className="text-sm">Starte die Konversation!</p>
                                    </div>
                                )}
                                {messages.map((msg, idx) => {
                                    const isOwn = msg.sender_id === user?.id;
                                    const showAvatar = !isOwn && (idx === 0 || messages[idx - 1]?.sender_id !== msg.sender_id);
                                    const showTime = idx === messages.length - 1 || messages[idx + 1]?.sender_id !== msg.sender_id;
                                    const prevMsg = messages[idx - 1];
                                    const isNewSender = idx === 0 || prevMsg?.sender_id !== msg.sender_id;
                                    const isRead = msg.read_by?.length > 1;
                                    
                                    return (
                                        <motion.div
                                            key={msg.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${isNewSender ? 'mt-4' : 'mt-0.5'} group`}
                                        >
                                            {!isOwn && showAvatar && (
                                                <Popover>
                                                    <PopoverTrigger asChild>
                                                        <Avatar className="w-8 h-8 mt-1 shrink-0 mr-2 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all">
                                                            {msg.sender?.profile_image ? (
                                                                <AvatarImage src={msg.sender.profile_image} />
                                                            ) : (
                                                                <AvatarFallback style={{ backgroundColor: msg.sender?.profile_color || '#009dde' }} className="text-white text-xs">
                                                                    {msg.sender?.first_name?.[0]}{msg.sender?.last_name?.[0]}
                                                                </AvatarFallback>
                                                            )}
                                                        </Avatar>
                                                    </PopoverTrigger>
                                                    <PopoverContent side="right" align="start" className="w-64 p-0">
                                                        <div className="p-4">
                                                            <div className="flex items-center gap-3 mb-3">
                                                                <Avatar className="w-12 h-12">
                                                                    {msg.sender?.profile_image ? (
                                                                        <AvatarImage src={msg.sender.profile_image} />
                                                                    ) : (
                                                                        <AvatarFallback style={{ backgroundColor: msg.sender?.profile_color || '#009dde' }} className="text-white">
                                                                            {msg.sender?.first_name?.[0]}{msg.sender?.last_name?.[0]}
                                                                        </AvatarFallback>
                                                                    )}
                                                                </Avatar>
                                                                <div>
                                                                    <p className="font-medium">{msg.sender?.first_name} {msg.sender?.last_name}</p>
                                                                    <p className="text-xs text-muted-foreground capitalize">{msg.sender?.role}</p>
                                                                    <div className="flex items-center gap-1 mt-1">
                                                                        <div className={`w-2 h-2 rounded-full ${isUserOnline(msg.sender_id) ? 'bg-green-500' : 'bg-gray-400'}`} />
                                                                        <span className="text-xs text-muted-foreground">
                                                                            {isUserOnline(msg.sender_id) ? 'Online' : 'Offline'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <Button size="sm" variant="outline" className="flex-1" onClick={() => startConversationWithUser(msg.sender_id)}>
                                                                    <MessageCircle className="w-4 h-4 mr-1" />
                                                                    Chat
                                                                </Button>
                                                                <Button size="sm" variant="outline" className="flex-1" onClick={() => { startConversationWithUser(msg.sender_id).then(() => startVideoCall()); }}>
                                                                    <Video className="w-4 h-4 mr-1" />
                                                                    Anruf
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </PopoverContent>
                                                </Popover>
                                            )}
                                            {!isOwn && !showAvatar && <div className="w-10 shrink-0" />}
                                            
                                            <div className={`max-w-[75%] ${isOwn ? 'order-1' : ''}`}>
                                                {showAvatar && !isOwn && (activeConversation.is_group || activeConversation.is_team_chat) && (
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <p className="text-xs text-muted-foreground mb-1 ml-1 cursor-pointer hover:text-primary transition-colors">
                                                                {msg.sender?.first_name} {msg.sender?.last_name}
                                                            </p>
                                                        </PopoverTrigger>
                                                        <PopoverContent side="top" align="start" className="w-64 p-0">
                                                            <div className="p-4">
                                                                <div className="flex items-center gap-3 mb-3">
                                                                    <Avatar className="w-12 h-12">
                                                                        {msg.sender?.profile_image ? (
                                                                            <AvatarImage src={msg.sender.profile_image} />
                                                                        ) : (
                                                                            <AvatarFallback style={{ backgroundColor: msg.sender?.profile_color || '#009dde' }} className="text-white">
                                                                                {msg.sender?.first_name?.[0]}{msg.sender?.last_name?.[0]}
                                                                            </AvatarFallback>
                                                                        )}
                                                                    </Avatar>
                                                                    <div>
                                                                        <p className="font-medium">{msg.sender?.first_name} {msg.sender?.last_name}</p>
                                                                        <p className="text-xs text-muted-foreground capitalize">{msg.sender?.role}</p>
                                                                        <div className="flex items-center gap-1 mt-1">
                                                                            <div className={`w-2 h-2 rounded-full ${isUserOnline(msg.sender_id) ? 'bg-green-500' : 'bg-gray-400'}`} />
                                                                            <span className="text-xs text-muted-foreground">
                                                                                {isUserOnline(msg.sender_id) ? 'Online' : 'Offline'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex gap-2">
                                                                    <Button size="sm" variant="outline" className="flex-1" onClick={() => startConversationWithUser(msg.sender_id)}>
                                                                        <MessageCircle className="w-4 h-4 mr-1" />
                                                                        Chat
                                                                    </Button>
                                                                    <Button size="sm" variant="outline" className="flex-1" onClick={() => { startConversationWithUser(msg.sender_id).then(() => startVideoCall()); }}>
                                                                        <Video className="w-4 h-4 mr-1" />
                                                                        Anruf
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </PopoverContent>
                                                    </Popover>
                                                )}
                                                
                                                <div className="relative">
                                                    {/* Message Actions */}
                                                    {isOwn && (
                                                        <div className={`absolute -left-16 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 flex gap-1`}>
                                                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditingMessage(msg); setEditContent(msg.content); }}>
                                                                <Edit3 className="w-3 h-3" />
                                                            </Button>
                                                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDeleteMessage(msg.id)}>
                                                                <Trash2 className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                    
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <div
                                                                className={`px-3.5 py-2.5 cursor-pointer ${
                                                                    isOwn 
                                                                        ? 'rounded-2xl rounded-br-sm shadow-md' 
                                                                        : 'bg-card border border-border/60 rounded-2xl rounded-bl-sm shadow-sm'
                                                                }`}
                                                                style={isOwn ? { backgroundColor: currentTheme.bubbleColor, color: 'white' } : {}}
                                                            >
                                                                {/* Voice Message */}
                                                                {msg.message_type === 'voice' ? (
                                                                    <VoiceMessagePlayer 
                                                                        src={msg.attachment?.url || msg.voice_url} 
                                                                        isOwn={isOwn}
                                                                        themeColor={currentTheme.bubbleColor}
                                                                    />
                                                                ) : msg.message_type === 'image' ? (
                                                                    <div>
                                                                        <img src={msg.attachment?.path} alt="" className="max-w-full rounded-lg" />
                                                                        <p className="text-xs mt-1 opacity-70">{msg.content}</p>
                                                                    </div>
                                                                ) : msg.message_type === 'file' ? (
                                                                    <div className="flex items-center gap-2">
                                                                        <Paperclip className="w-4 h-4" />
                                                                        <span>{msg.content}</span>
                                                                        <Button size="icon" variant="ghost" className="h-6 w-6">
                                                                            <Download className="w-3 h-3" />
                                                                        </Button>
                                                                    </div>
                                                                ) : (
                                                                    <p className="whitespace-pre-wrap break-words text-[15px] leading-snug">{msg.content}</p>
                                                                )}
                                                                
                                                                {showTime && (
                                                                    <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                                                                        <span className={`text-[10px] ${isOwn ? 'text-white/70' : 'text-muted-foreground'}`}>
                                                                            {format(new Date(msg.created_at), 'HH:mm')}
                                                                            {msg.edited_at && ' (bearbeitet)'}
                                                                        </span>
                                                                        {isOwn && (
                                                                            isRead 
                                                                                ? <CheckCheck className="w-3.5 h-3.5 text-blue-300" /> 
                                                                                : <Check className="w-3.5 h-3.5 text-white/50" />
                                                                        )}
                                                                    </div>
                                                                )}
                                                                
                                                                {msg.reactions?.length > 0 && (
                                                                    <div className={`flex flex-wrap gap-1 mt-1.5 ${isOwn ? 'justify-end' : ''}`}>
                                                                        {Object.entries(msg.reactions.reduce((acc, r) => { acc[r.emoji] = (acc[r.emoji] || 0) + 1; return acc; }, {})).map(([emoji, count]) => (
                                                                            <span key={emoji} className="text-xs bg-background/80 px-1.5 py-0.5 rounded-full border cursor-pointer hover:scale-110 transition-transform" onClick={(e) => { e.stopPropagation(); handleReaction(msg.id, emoji); }}>
                                                                                {emoji} {count > 1 && count}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-auto p-2" side={isOwn ? 'left' : 'right'}>
                                                            <div className="flex gap-1">
                                                                {EMOJI_REACTIONS.map(emoji => (
                                                                    <button key={emoji} onClick={() => handleReaction(msg.id, emoji)} className="text-xl hover:scale-125 transition-transform p-1">
                                                                        {emoji}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </PopoverContent>
                                                    </Popover>
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                                
                                {getTypingText() && (
                                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 mt-4">
                                        <div className="w-10" />
                                        <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-2">
                                            <div className="flex gap-1">
                                                <div className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                                                <div className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                                                <div className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '300ms' }} />
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>
                        </ScrollArea>
                        
                        {/* Input */}
                        <div className="px-4 py-3 border-t border-border bg-card/80 backdrop-blur-sm">
                            {/* Edit Mode */}
                            {editingMessage && (
                                <div className="flex items-center gap-2 mb-3 p-2 bg-primary/10 rounded-xl border border-primary/20">
                                    <Edit3 className="w-4 h-4 text-primary" />
                                    <span className="text-sm flex-1 truncate">Bearbeiten: {editingMessage.content}</span>
                                    <Button size="icon" variant="ghost" className="h-6 w-6 rounded-full" onClick={() => setEditingMessage(null)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}
                            
                            <form onSubmit={editingMessage ? (e) => { e.preventDefault(); handleEditMessage(); } : handleSendMessage} className="flex items-end gap-2">
                                <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx" />
                                
                                <div className="flex-1 relative">
                                    <Input
                                        ref={inputRef}
                                        value={editingMessage ? editContent : newMessage}
                                        onChange={editingMessage ? (e) => setEditContent(e.target.value) : handleInputChange}
                                        placeholder={editingMessage ? "Nachricht bearbeiten..." : "Nachricht schreiben..."}
                                        className="pr-24 py-6 rounded-2xl bg-muted/30 border-muted-foreground/10 focus:border-primary/30 focus:ring-1 focus:ring-primary/20 transition-all"
                                        data-testid="message-input"
                                    />
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-0.5">
                                        <Button type="button" size="icon" variant="ghost" className="h-9 w-9 rounded-full hover:bg-muted">
                                            <Smile className="w-5 h-5 text-muted-foreground" />
                                        </Button>
                                        <Button type="button" size="icon" variant="ghost" className="h-9 w-9 rounded-full hover:bg-muted" onClick={() => fileInputRef.current?.click()}>
                                            <Paperclip className="w-5 h-5 text-muted-foreground" />
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Voice or Send */}
                                {!editingMessage && !newMessage.trim() ? (
                                    <Button
                                        type="button"
                                        size="icon"
                                        className={`h-12 w-12 rounded-full shrink-0 shadow-lg transition-all ${isRecording ? 'bg-red-500 animate-pulse scale-110' : 'hover:scale-105'}`}
                                        onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
                                    >
                                        {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                                    </Button>
                                ) : (
                                    <Button 
                                        type="submit" 
                                        size="icon" 
                                        className="h-12 w-12 rounded-full shrink-0 shadow-lg hover:scale-105 transition-transform" 
                                        style={{ backgroundColor: currentTheme.bubbleColor }}
                                        disabled={editingMessage ? !editContent.trim() : (!newMessage.trim() || sendingMessage)}
                                    >
                                        <Send className="w-5 h-5" />
                                    </Button>
                                )}
                            </form>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center bg-gradient-to-b from-background to-muted/30">
                        <div className="text-center max-w-md px-6">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                                <MessageCircle className="w-12 h-12 text-primary/50" />
                            </div>
                            <h2 className="text-xl font-semibold mb-2">Wähle eine Konversation</h2>
                            <p className="text-muted-foreground mb-6">Wähle einen Chat aus der Liste oder starte eine neue Unterhaltung</p>
                            <div className="flex gap-3 justify-center">
                                <Button variant="default" onClick={() => setShowNewChat(true)} className="rounded-xl">
                                    <Plus className="w-4 h-4 mr-2" />
                                    Neuer Chat
                                </Button>
                                <Button variant="outline" onClick={() => setShowNewGroup(true)} className="rounded-xl">
                                    <Users className="w-4 h-4 mr-2" />
                                    Neue Gruppe
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Dialogs */}
            <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5" />
                            Neue Nachricht
                        </DialogTitle>
                    </DialogHeader>
                    <Input placeholder="Benutzer suchen..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                    <ScrollArea className="h-64">
                        <div className="space-y-1">
                            {allUsers.filter(u => !searchQuery || `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())).map(u => (
                                <div key={u.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer" onClick={() => handleStartNewChat(u)}>
                                    <div className="relative">
                                        <Avatar>
                                            {u.profile_image ? <AvatarImage src={u.profile_image} /> : <AvatarFallback style={{ backgroundColor: u.profile_color }} className="text-white">{u.first_name?.[0]}{u.last_name?.[0]}</AvatarFallback>}
                                        </Avatar>
                                        {isUserOnline(u.id) && <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 border-2 border-background" />}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium">{u.first_name} {u.last_name}</p>
                                        <p className="text-xs text-muted-foreground">{u.role}</p>
                                    </div>
                                    {isUserOnline(u.id) && <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">Online</Badge>}
                                </div>
                            ))}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>

            <Dialog open={showNewGroup} onOpenChange={setShowNewGroup}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Neue Gruppe erstellen
                        </DialogTitle>
                    </DialogHeader>
                    <Input placeholder="Gruppenname..." value={groupName} onChange={(e) => setGroupName(e.target.value)} />
                    <p className="text-sm font-medium">Teilnehmer auswählen:</p>
                    <ScrollArea className="h-48 border rounded-lg p-2">
                        {allUsers.map(u => (
                            <div key={u.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${selectedGroupMembers.includes(u.id) ? 'bg-primary/10' : 'hover:bg-muted'}`} onClick={() => setSelectedGroupMembers(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])}>
                                <Checkbox checked={selectedGroupMembers.includes(u.id)} />
                                <Avatar className="w-8 h-8"><AvatarFallback style={{ backgroundColor: u.profile_color }} className="text-white text-xs">{u.first_name?.[0]}{u.last_name?.[0]}</AvatarFallback></Avatar>
                                <span className="text-sm">{u.first_name} {u.last_name}</span>
                            </div>
                        ))}
                    </ScrollArea>
                    <Button className="w-full" onClick={handleCreateGroup} disabled={!groupName.trim() || selectedGroupMembers.length < 1}>
                        Gruppe erstellen ({selectedGroupMembers.length + 1} Teilnehmer)
                    </Button>
                </DialogContent>
            </Dialog>

            <Dialog open={showChatInfo} onOpenChange={setShowChatInfo}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Info className="w-5 h-5" />
                            Chat-Info
                        </DialogTitle>
                    </DialogHeader>
                    {chatInfoData && (
                        <div className="space-y-4">
                            <div className="text-center pb-4 border-b">
                                <Avatar className="w-20 h-20 mx-auto mb-3">
                                    <AvatarFallback style={{ backgroundColor: chatInfoData.participant?.profile_color || '#009dde' }} className="text-white text-2xl">
                                        {activeConversation?.is_group ? <Users className="w-8 h-8" /> : `${chatInfoData.participant?.first_name?.[0]}${chatInfoData.participant?.last_name?.[0]}`}
                                    </AvatarFallback>
                                </Avatar>
                                <h3 className="text-lg font-semibold">{getConversationName(activeConversation)}</h3>
                            </div>
                            
                            {chatInfoData.shared_projects?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                        <FolderKanban className="w-4 h-4" />
                                        Gemeinsame Projekte ({chatInfoData.shared_projects.length})
                                    </h4>
                                    {chatInfoData.shared_projects.map(project => (
                                        <div key={project.id} className="p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted mb-1" onClick={() => { setShowChatInfo(false); navigate(`/projects?id=${project.id}`); }}>
                                            <p className="text-sm font-medium">{project.name}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {chatInfoData.shared_groups?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                        <Users className="w-4 h-4" />
                                        Gemeinsame Gruppen ({chatInfoData.shared_groups.length})
                                    </h4>
                                    {chatInfoData.shared_groups.map(group => (
                                        <div key={group.id} className="p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted mb-1" onClick={() => { setShowChatInfo(false); setActiveConversation(group); loadMessages(group.id); }}>
                                            <p className="text-sm font-medium">{group.name}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            <div className="flex gap-2 pt-4 border-t">
                                <Button variant="outline" className="flex-1" onClick={startVideoCall}>
                                    <Video className="w-4 h-4 mr-2" />
                                    Video-Call
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Themes Dialog */}
            <Dialog open={showThemes} onOpenChange={setShowThemes}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Palette className="w-5 h-5" />
                            Chat-Theme
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        {CHAT_THEMES.map(theme => (
                            <div
                                key={theme.id}
                                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${chatSettings.theme === theme.id ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/50'}`}
                                onClick={() => updateChatSettings({ chat_theme: theme.id, theme: theme.id })}
                            >
                                <div className="w-full h-12 rounded-lg mb-2" style={{ backgroundColor: theme.bubbleColor }} />
                                <p className="text-sm font-medium text-center">{theme.name}</p>
                            </div>
                        ))}
                    </div>
                    <div className="space-y-3 pt-4 border-t">
                        <div className="flex items-center justify-between">
                            <Label className="flex items-center gap-2">
                                <Volume2 className="w-4 h-4" />
                                Sounds
                            </Label>
                            <Switch checked={chatSettings.sound_enabled} onCheckedChange={(v) => updateChatSettings({ chat_sound_enabled: v, sound_enabled: v })} />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ChatPage;
